/**
 * PredictiveSearch
 *
 * Handles Shopify-style predictive search UI, result fetching, and user interaction.
 * Manages search query updates, result panel rendering, resource (tab) switching, and accessibility.
 *
 * @module PredictiveSearch
 */
(function PredictiveSearch() {
  'use strict';

  /**
   * CSS selectors used throughout the PredictiveSearch module.
   * @constant {Object<string, string>}
   */
  const SELECTORS = {
    searchInput: '[data-search-input]',
    resultsContainer: '[data-predictive-search-container]',
    markup: '[data-predictive-search-markup]',
    searchTypeInput: '[data-search-type]',
    searchTypeChecked: '[data-search-type]:checked',
    resources: '[data-predictive-search-resources]',
    predictiveSearchLink: 'a[data-predictive-search-link]',
    scrim: '#Scrim'
  };

  /**
   * Custom events emitted by PredictiveSearch.
   * @constant {Object<string, string>}
   */
  const EVENTS = {
    /** Event emitted when the search query changes. */
    queryUpdated: 'predictive:queryUpdated',
    /** Event emitted when the search results HTML is updated. 
     * Returns a custom PredictiveSearchResult https://shopify.dev/docs/api/storefront/latest/objects/PredictiveSearchResult
    */
    resultsUpdated: 'predictive:resultsUpdated',
  }

  // ----- Cached DOM elements -----
  /** @type {HTMLInputElement|null} */
  const searchInput = document.querySelector(SELECTORS.searchInput);
  /** @type {HTMLElement|null} */
  const resultsContainer = document.querySelector(SELECTORS.resultsContainer);
  /** @type {HTMLElement|null} */
  const scrim = document.querySelector(SELECTORS.scrim);

  /**
   * The public API for PredictiveSearch.
   * @namespace
   */
  const publicAPI = {
    /** Resets the search results UI to initial/empty state. */
    resetResults,
    /** References to core elements. */
    elements: {
      searchInput,
      resultsContainer
    },
    SELECTORS,
    EVENTS
  }

  window.POV = window.POV || {};
  /** @type {typeof publicAPI} */
  window.POV.PredictiveSearch = publicAPI;

  if (!searchInput || !resultsContainer) {
    // Stop if required elements are missing
    return;
  }

  // ----- Fetch control -----
  /** @type {AbortController|null} */
  let abortController = null;
  /** @type {number} */
  let requestCounter = 0; // Unique id for each input event

  // ----- Event listeners -----
  searchInput.addEventListener('input', debounce(onSearchInput, 128));

  // =========================
  //        Functions
  // =========================

  /**
   * Handles search input events: triggers search, aborts old fetches, manages state.
   * @async
   * @returns {Promise<void>}
   */
  async function onSearchInput() {
    const query = searchInput.value.trim();

    if (!query) {
      resetResults();
      abortOngoingFetch();
      return;
    }

    POV.emit(EVENTS.queryUpdated, {
      "query": {
        "text": query,
        "__typename": "CustomSearchQuery"
      }
    });

    // Cancel any previous fetch
    abortOngoingFetch();

    // Prepare for new fetch
    abortController = new AbortController();
    const currentRequest = ++requestCounter;

    const searchUrl = buildSearchUrl(query);

    try {
      const response = await fetch(searchUrl, { signal: abortController.signal });
      if (!response.ok) throw new Error('Network response was not ok');
      const html = await response.text();

      // Only process if this is the latest input
      if (currentRequest === requestCounter) {
        updateResults(html);
        setupContentTypeEvents();
      }
    } catch (err) {
      if (err.name !== 'AbortError' && currentRequest === requestCounter) {
        // Optional: handle/display errors here
        // console.error('Predictive search error:', err);
      }
    } finally {
      if (currentRequest === requestCounter) abortController = null;
    }
  }

  /**
   * Aborts any ongoing predictive search fetch.
   */
  function abortOngoingFetch() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  }

  /**
   * Constructs the predictive search URL for a given query.
   * @param {string} query - The search query string.
   * @returns {string} The URL to fetch predictive search results.
   */
  function buildSearchUrl(query) {
    const baseUrl = routes.predictive_search_url;
    const params = new URLSearchParams({
      q: query,
      section_id: 'predictive-search',
      'resources[type]': 'product,query,article',
      'resources[limit_scope]': 'each'
    });
    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Updates the results panel DOM with new predictive search results markup.
   * Emits {@link EVENTS.resultsUpdated}.
   * @param {string} html - The HTML string returned by the predictive search API.
   */
  function updateResults(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const markup = doc.querySelector(SELECTORS.markup);
    if (markup) {
      resultsContainer.innerHTML = markup.innerHTML;
      resultsContainer.removeAttribute('aria-hidden');
      showScrim();
    } else {
      resetResults();
    }

    const trackingData = JSON.parse(markup.querySelector('[data-predictive-search-tracking]').innerHTML);
    POV.emit(EVENTS.resultsUpdated, trackingData);
  }

  /**
   * Resets the predictive search results UI to its initial state.
   */
  function resetResults() {
    resultsContainer.setAttribute('aria-hidden', 'true');
    resultsContainer.innerHTML = '';
    resultsContainer.removeAttribute('style');
    hideScrim();
  }

  /**
   * Attaches content-type change event listeners to filter/search-type inputs (e.g. product/article tabs).
   * Ensures that only the selected resource type is shown.
   */
  function setupContentTypeEvents() {
    // To avoid duplicate listeners, always remove then add
    const searchTypeInputs = Array.from(resultsContainer.querySelectorAll(SELECTORS.searchTypeInput));
    searchTypeInputs.forEach(input => {
      input.removeEventListener('input', onContentTypeChange);
      input.addEventListener('input', onContentTypeChange);
    });
  }

  /**
   * Handles user changing the predictive search resource type (e.g., tabs).
   * Updates resource visibility and modifies links to include the selected search type.
   */
  function onContentTypeChange() {
    const selectedInput = resultsContainer.querySelector(SELECTORS.searchTypeChecked);
    if (!selectedInput) return;

    const searchType = selectedInput.value;
    const resources = Array.from(resultsContainer.querySelectorAll(SELECTORS.resources));

    resources.forEach(resource => {
      if (resource.dataset.predictiveSearchResources === searchType) {
        resource.removeAttribute('hidden');
      } else {
        resource.setAttribute('hidden', 'true');
      }
    });

    updateSearchTypeLinks(searchType);
  }

  /**
   * Updates all predictive search links to include the currently selected search type as a URL parameter.
   * @param {string} searchType - The search type (e.g., 'product', 'article', etc.)
   */
  function updateSearchTypeLinks(searchType) {
    const links = document.querySelectorAll(SELECTORS.predictiveSearchLink);
    links.forEach(link => {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('type', searchType);
      link.href = url.toString();
    });
  }

  /**
   * Shows the predictive search overlay/scrim for accessibility and focus control.
   */
  function showScrim() {
    if (scrim) {
      scrim.setAttribute('aria-hidden', 'false');
      scrim.addEventListener('click', resetResults, { once: true });
    }
  }

  /**
   * Hides the predictive search overlay/scrim.
   */
  function hideScrim() {
    if (scrim) {
      scrim.setAttribute('aria-hidden', 'true');
    }
  }
})();
